// src/utils/api.js
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('bc_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('bc_token');
      localStorage.removeItem('bc_user');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export default api;

// ── Platform superadmin (multi-tenant control panel) ─────────────────────────
// Separate axios instance + token key so a platform-admin session and a tenant
// session can coexist in the same browser without clobbering each other.
const platformApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
});
platformApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('bc_platform_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
platformApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('bc_platform_token');
      window.location.reload(); // drop back to the platform login
    }
    return Promise.reject(err);
  }
);

// Which UI to render is decided by the host — this call is unauthenticated.
export const getPlatformContext = () => api.get('/platform-context');

export const platformLogin   = (email, password) => platformApi.post('/platform-auth/login', { email, password });
export const getPlatformMe   = () => platformApi.get('/platform-auth/me');
export const platformChangePassword = (old_password, new_password) => platformApi.post('/platform-auth/change-password', { old_password, new_password });

export const getTenants       = () => platformApi.get('/platform/tenants');
export const createTenant     = (data) => platformApi.post('/platform/tenants', data);
export const provisionTenant  = (id) => platformApi.post(`/platform/tenants/${id}/provision`);
export const resetTenantAdmin = (id, admin_email) => platformApi.post(`/platform/tenants/${id}/reset-admin`, { admin_email });
export const suspendTenant    = (id) => platformApi.post(`/platform/tenants/${id}/suspend`);
export const activateTenant   = (id) => platformApi.post(`/platform/tenants/${id}/activate`);
export const webuzoCheck      = () => platformApi.get('/platform/webuzo-check');

// Console: usage / impersonation / lifecycle
export const getTenantUsage   = (id) => platformApi.get(`/platform/tenants/${id}/usage`);
export const getTenantUsers   = (id) => platformApi.get(`/platform/tenants/${id}/users`);
export const impersonateTenantUser = (id, user_id) => platformApi.post(`/platform/tenants/${id}/impersonate`, { user_id });
export const deleteTenant     = (id) => platformApi.post(`/platform/tenants/${id}/delete`);
export const restoreTenant    = (id) => platformApi.post(`/platform/tenants/${id}/restore`);
export const purgeTenant      = (id, confirm) => platformApi.post(`/platform/tenants/${id}/purge`, { confirm });

// Backup download — the platform token lives in localStorage, not a cookie, so a
// plain <a href> can't authenticate. Fetch with the header, save via blob
// (same approach as downloadCSV below).
export const downloadTenantBackup = async (id, subdomain) => {
  const token = localStorage.getItem('bc_platform_token');
  const base  = process.env.REACT_APP_API_URL || '/api';
  const res = await fetch(`${base}/platform/tenants/${id}/backup`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    let msg = 'Backup failed';
    try { msg = (await res.json()).error || msg; } catch {}
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${subdomain}-backup-${new Date().toISOString().slice(0, 10)}.zip`;
  a.click();
  URL.revokeObjectURL(url);
};

// Tenant-side: redeem a one-time impersonation code for a session.
export const impersonateExchange = (code) => api.post('/auth/impersonate-exchange', { code });

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const getMe = () => api.get('/auth/me');
export const changePassword = (old_password, new_password) => api.post('/auth/change-password', { old_password, new_password });
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, password) => api.post('/auth/reset-password', { token, password });

// Dashboard
export const getDashboard = () => api.get('/dashboard');

// Leads
export const getLeads = (params) => api.get('/leads', { params });
export const getLead = (id) => api.get(`/leads/${id}`);
export const createLead = (data) => api.post('/leads', data);
export const updateLead = (id, data) => api.put(`/leads/${id}`, data);
export const deleteLead = (id) => api.delete(`/leads/${id}`);
export const addTimeline = (id, data) => api.post(`/leads/${id}/timeline`, data);
export const setFollowup = (id, data) => api.patch(`/leads/${id}/followup`, data);

// Settings
export const getSettings = () => api.get('/settings');
export const saveSettings = (data) => api.post('/settings', data);
export const sendTestEmail = (data) => api.post('/test-email', data);

// Branding (public — no auth required, but harmless if a token is attached)
export const getBranding = () => api.get('/branding');
export const uploadBrandingImage = (kind, file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post(`/branding-image?kind=${kind}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const fetchBrandingImageFromUrl = (kind, url) => api.post(`/branding-image?kind=${kind}`, { url });

// Services
export const getServices = () => api.get('/services');
export const addService = (name) => api.post('/services', { name });
export const removeService = (name) => api.delete('/services', { data: { name } });

// Lead statuses / sources (per-client configurable pipeline)
export const getLeadStatuses   = ()         => api.get('/lead-statuses');
export const createLeadStatus  = (data)     => api.post('/lead-statuses', data);
export const updateLeadStatus  = (id, data) => api.put(`/lead-statuses/${id}`, data);
export const deleteLeadStatus  = (id)       => api.delete(`/lead-statuses/${id}`);

export const getLeadSources    = ()         => api.get('/lead-sources');
export const createLeadSource  = (data)     => api.post('/lead-sources', data);
export const updateLeadSource  = (id, data) => api.put(`/lead-sources/${id}`, data);
export const deleteLeadSource  = (id)       => api.delete(`/lead-sources/${id}`);

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);
export const adminSetPassword = (id, password) => api.post(`/users/${id}/set-password`, { password });
export const adminGenerateResetLink = (id) => api.post(`/users/${id}/reset-link`);

// Roles & Permissions
export const getRoles          = ()         => api.get('/roles');
export const getPermissionCatalog = ()      => api.get('/roles/permissions');
export const createRole        = (data)     => api.post('/roles', data);
export const updateRole        = (id, data) => api.put(`/roles/${id}`, data);
export const deleteRole        = (id)       => api.delete(`/roles/${id}`);

// Import / Export
export const importCSV = (file) => {
  const form = new FormData();
  form.append('file', file);
  return api.post('/import-csv', form, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const downloadCSV = async (params = {}) => {
  const token = localStorage.getItem('bc_token');
  const base = process.env.REACT_APP_API_URL || '/api';
  const qs = new URLSearchParams(params).toString();
  const res = await fetch(`${base}/export-csv${qs ? `?${qs}` : ''}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `crm_leads_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// Forms (webhook field-mapping profiles)
export const getForms         = ()         => api.get('/forms');
export const getForm          = (id)       => api.get(`/forms/${id}`);
export const createForm       = (data)     => api.post('/forms', data);
export const updateForm       = (id, data) => api.put(`/forms/${id}`, data);
export const deleteForm       = (id)       => api.delete(`/forms/${id}`);
export const getFormLogs      = (id)       => api.get(`/forms/${id}/logs`);
export const getUnmatchedLogs = ()         => api.get('/forms/unmatched-logs');
export const getAllWebhookLogs = ()         => api.get('/forms/all-logs');

// Webhook secret (single, shared across all forms)
export const getWebhookSecret        = () => api.get('/webhook-secret');
export const regenerateWebhookSecret = () => api.post('/webhook-secret/regenerate');

// Custom domain (multi-tenant mode only — 404s otherwise, callers should
// treat that as "feature not enabled on this install", not an error to show)
export const getDomain    = ()       => api.get('/domain');
export const saveDomain   = (domain) => api.post('/domain', { custom_domain: domain });
export const verifyDomain = ()       => api.post('/domain/verify');

// Facebook / Meta Lead Ads — one-click connect. The user never handles a token:
// connectStart returns a Facebook authorize URL to open in a popup, and once
// that popup reports back, the same `state` is used to list and save pages.
export const getMetaStatus       = ()       => api.get('/meta/status');
export const metaConnectStart    = ()       => api.post('/meta/connect-start');
export const getMetaConnectState = (state)  => api.get('/meta/connect-status', { params: { state } });
export const getMetaPages        = (state)  => api.get('/meta/pages', { params: { state } });
export const saveMetaPages       = (state, pageIds) => api.post('/meta/pages', { state, page_ids: pageIds });
export const disconnectMetaPage  = (pageId) => api.post('/meta/disconnect', { page_id: pageId });
